import { TextField } from "@mui/material";
import React, { FunctionComponent, useCallback, useEffect, useRef } from "react";
import { styled } from "@iventis/styles";
import { useKeyboardEvent, useClickDetectOutside } from "@iventis/utilities";
import { Theme } from "@emotion/react";
import { fontSizes } from "@iventis/styles/src/atomic-rules";
import { SimpleTooltip } from "./tooltips";

interface ItemNameProps {
    name: string;
    visible?: boolean;
    editName: boolean;
    allowEditName: boolean;
    className: string;
    changeItemName: (newName: string) => void;
    setEditName: (value: boolean) => void;
    allowSingleClickEdit: boolean;
}

/**
 * A component that shows a name and when double clicked allows the name to edited
 * @param {string} name - name of the item
 * @param {boolean} editName - if the name is being edited
 * @param { boolean } allowEditName - if the name is allowed to be changed
 * @param {(newName: string) => void} setEditName - a function to handle when the name is double clicked
 * @param {(value: boolean) => void;} changeItemName - a function to handle when the name is changed
 */
const ItemNameComponent: FunctionComponent<ItemNameProps> = ({ name, editName, allowEditName, allowSingleClickEdit, className, setEditName, changeItemName, visible = true }) => {
    // reference for the name of item
    const nameReference = useRef(name);
    // reference for the input box
    const inputRef = useRef<HTMLDivElement>(null);

    // reference for the name label
    const nameLabelRef = useRef<HTMLParagraphElement>(null);

    // reference for name container
    const nameContainerRef = useRef<HTMLDivElement>(null);

    // event handlers for outside component click and keyboard events
    useClickDetectOutside((event) => {
        // Stops space bar press triggering the onClick event
        if (event.pointerType !== "") {
            changeItemName(nameReference.current == null ? name : nameReference.current);
        }
    }, inputRef);

    useKeyboardEvent(() => changeItemName(nameReference.current), editName, "Enter", inputRef?.current);
    useKeyboardEvent(() => changeItemName(name), editName, "Escape", inputRef?.current);

    const blockEdit = useRef<boolean>(false);

    const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Control") {
            blockEdit.current = true;
        }
    };

    const onKeyUp = (event: KeyboardEvent) => {
        if (event.key === "Control") {
            blockEdit.current = false;
        }
    };

    document.body.addEventListener("keydown", onKeyDown);

    document.body.addEventListener("keyup", onKeyUp);

    useEffect(
        () => () => {
            // eslint-disable-next-line no-unused-expressions
            document.body.removeEventListener("keydown", onKeyDown);
            document.body.removeEventListener("keyup", onKeyUp);
        },
        []
    );

    // when the name is changed
    const handleNameChange = useCallback((name: string) => {
        nameReference.current = name;
    }, []);

    const shouldHideTooltip = (editName && allowEditName) || (nameLabelRef.current?.scrollWidth ?? 0) < (nameContainerRef.current?.scrollWidth ?? 0);

    return (
        <StyledOverflowTooltip text={name} placement="bottom-end" disabled={shouldHideTooltip}>
            <NameContainer>
                {editName && allowEditName ? (
                    <ItemNameInput
                        ref={inputRef}
                        variant="outlined"
                        defaultValue={name}
                        // Prevent dragging while the user is editing name
                        onMouseDown={(e) => e.stopPropagation()}
                        onChange={(event) => handleNameChange(event.target.value)}
                        autoFocus
                    />
                ) : (
                    <StyledName
                        onClick={(e) => {
                            if (allowSingleClickEdit && allowEditName && !blockEdit.current) {
                                setEditName(true);
                                e.stopPropagation();
                                e.preventDefault();
                            }
                        }}
                        onDoubleClick={(e) => {
                            if (allowSingleClickEdit && allowEditName && !blockEdit.current) {
                                setEditName(true);
                                e.stopPropagation();
                                e.preventDefault();
                            }
                        }}
                        visible={visible}
                        ref={nameContainerRef}
                    >
                        <p className={className} ref={nameLabelRef}>
                            {name}
                        </p>
                    </StyledName>
                )}
            </NameContainer>
        </StyledOverflowTooltip>
    );
};

const NameContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    height: 100%;
    width: 100%;
    user-select: none;
    min-width: 0;
`;

const ItemNameInput = styled(TextField)`
    height: 80%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    margin-left: -5px;
    .MuiInputBase-input {
        padding: 10px 5px;
        font-size: ${fontSizes.summarySmall};
        font-weight: 400;
        outline: none;
        background-color: white;
    }
    .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }: { theme: Theme }) => theme.shades.two};
        border-width: 1px;
    }
`;

const StyledName = styled.div<{ visible: boolean }>`
    width: 100%;
    display: flex;
    p {
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
        color: ${({ visible, theme }: { visible: boolean; theme: Theme }) => (visible ? "initial" : theme.typographyColors.moreSubdued)};
        text-align: left;
    }
`;

export const StyledOverflowTooltip = styled(SimpleTooltip)`
    & .MuiTooltip-tooltip {
        padding: 8px;
        font-size: ${fontSizes.small};
        font-weight: normal;
        line-height: 20px;
        text-align: left;
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.core};
        background-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
        box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.25);
    }
    & .MuiTooltip-arrow {
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
    }
`;

export default ItemNameComponent;
